import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Sprout } from "lucide-react";

interface Props {
  seasonLine: string | null;
  rhythm: ReactNode;
  controls: ReactNode;
  now?: () => Date;
}

/** Date + season note on the start side, rhythm line + mode switch on the end side (spec 2026-11-04 §2.1).
 *  On narrow screens the rhythm wraps under the date and the switch stays on the end side. */
const TodayTopLine = ({ seasonLine, rhythm, controls, now = () => new Date() }: Props) => {
  const { t, i18n } = useTranslation();
  const at = now();
  const date = at.toLocaleDateString(i18n.language, { weekday: "long", month: "long", day: "numeric" });
  // Below sm the long date wraps beside the mode switch; a short one stays on one line.
  const shortDate = at.toLocaleDateString(i18n.language, { weekday: "short", month: "short", day: "numeric" });
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      <div className="order-1 min-w-0 flex-1">
        <p className="font-serif text-[17px] leading-tight text-foreground">
          <span className="whitespace-nowrap sm:hidden">{shortDate}</span>
          <span className="hidden sm:inline">{date}</span>
        </p>
        {seasonLine && (
          <p className="mt-1.5 inline-flex max-w-full items-center gap-1.5 rounded-full bg-primary/[0.06] px-2.5 py-1 text-xs text-primary/90">
            <Sprout className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="truncate">{t("greeting_season", { line: seasonLine })}</span>
          </p>
        )}
      </div>
      <div className="order-3 basis-full lg:order-2 lg:basis-auto">{rhythm}</div>
      <div className="order-2 shrink-0 lg:order-3">{controls}</div>
    </div>
  );
};

export default TodayTopLine;
